import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import EmailAddress from "_shared/email-addresses/email-address";
import Guid from "_shared/guids/guid";
import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import { UserMemberInvitationNotFoundError } from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import AcceptOpenEntityMemberInvitationAPIRequest from "legal-entities/invitations/member-invitations/open/_api/commands/accept-open-entity-member-invitation-api-command";
import OpenEntityMemberInvitation from "legal-entities/invitations/member-invitations/open/open-entity-member-invitation";
import Session from "users/__session/session";

export default class OpenEntityMemberInvitationAPIService {
  public constructor(session: Readonly<Session>) {
    if (session.authToken) {
      this.headers.set("Authorization", `Bearer ${session.authToken.value}`);
    }
  }

  public async createOpenMemberInvitation(entityId: Guid): Promise<OpenEntityMemberInvitation> {
    const url = new URL(`/legal-entities/${entityId}/open-member-invitations`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
        },
      );
      return OpenEntityMemberInvitation.fromJSON(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest) {
        throw new InvalidInvitationError(error.response.data);
      }
      throw new OpenEntityMemberInvitationAPIServiceError("createOpenMemberInvitation", error);
    }
  }

  public async getAllOpenEntityMemberInvitationAsync(
    entityId: Guid,
    abortController?: AbortController,
  ): Promise<OpenEntityMemberInvitation[]> {
    const url = new URL(`/legal-entities/${entityId.value}/open-member-invitations`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: abortController?.signal,
      });
      return OpenEntityMemberInvitation.fromJSONArray(response.data) ?? [];
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      throw new OpenEntityMemberInvitationAPIServiceError("getAllOpenEntityMemberInvitationAsync", error);
    }
  }

  public async getOpenMemberInvitationById(
    id: Guid,
    abortController?: AbortController,
  ): Promise<OpenEntityMemberInvitation> {
    const url = new URL(`/legal-entities/open-member-invitations/${id.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: abortController?.signal,
      });
      return OpenEntityMemberInvitation.fromJSON(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
        throw new UserMemberInvitationNotFoundError(id);
      }
      if (error instanceof CanceledError) throw error;
      throw new OpenEntityMemberInvitationAPIServiceError("getOpenMemberInvitationById", error);
    }
  }

  public async acceptOpenEntityMemberInvitationAsync(
    invitation: OpenEntityMemberInvitation,
    email: EmailAddress,
  ): Promise<void> {
    if (!invitation.id) throw new Error("Invitation ID is required to accept an invitation.");

    const url = new URL(
      `/legal-entities/open-member-invitations/${invitation.id.value}/accept`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      await axios.post(url.toString(), new AcceptOpenEntityMemberInvitationAPIRequest(email), {
        headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
      });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
        throw new UserMemberInvitationNotFoundError(invitation.id);
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest)
        throw new InvalidInvitationError(error.response.data);
      throw new OpenEntityMemberInvitationAPIServiceError("acceptOpenEntityMemberInvitationAsync", error, invitation);
    }
  }

  public async deleteOpenMemberInvitation(invitation: OpenEntityMemberInvitation): Promise<void> {
    if (!invitation.id) throw new Error("Invitation ID is required to delete an invitation.");

    const url = new URL(
      `/legal-entities/${invitation.entity.id.value}/open-member-invitations/${invitation.id.value}`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      await axios.delete(url.toString(), {
        headers: this.headers,
      });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
        throw new UserMemberInvitationNotFoundError(invitation.id);
      throw new OpenEntityMemberInvitationAPIServiceError("deleteOpenMemberInvitation", error, invitation);
    }
  }

  private readonly headers: AxiosHeaders = new AxiosHeaders();
}

export class InvalidInvitationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidInvitationError";
  }
}

export class OpenEntityMemberInvitationAPIServiceError extends Error {
  invitation?: OpenEntityMemberInvitation;
  method: string;
  error: unknown;
  constructor(method: string, error: unknown, invitation?: OpenEntityMemberInvitation) {
    super(`OpenEntityMemberInvitationAPIService: ${error instanceof Error ? error.message : String(error)}`);
    this.method = method;
    this.error = error;
    this.invitation = invitation;
  }
}
